/**
 * LA TOUCHE « RETOUR » D'ANDROID FERME CE QUI EST OUVERT.
 *
 * Constate sur le Pixel 9 le 12 octobre: « retour » quittait le jeu pour la
 * page precedente du navigateur, chat ouvert ou non, panneau ouvert ou non. Le
 * jeu n'ecoutait pas `popstate` du tout.
 *
 * Chaque couche qui se ferme (panneau, chat, mode de placement) s'EMPILE en
 * ouvrant. « Retour » ferme celle du dessus. Rien d'ouvert: « retour » fait ce
 * qu'il a toujours fait, il quitte la page — c'est le choix de JC.
 *
 * UNE SEULE ENTREE DE GARDE dans l'historique, pas une par couche. La premiere
 * ecriture en posait une par couche et la retirait par `history.back()` a la
 * fermeture: or `back()` est ASYNCHRONE. Choisir un modele ferme le menu
 * (back en attente) et ouvre la barre de placement (pushState) dans le meme
 * geste; les deux se croisaient, et la fermeture de la barre faisait un retour
 * de trop — hors de la page (vu par la campagne de bout en bout: `__jeu`
 * disparu juste apres « Poser »). Ici, la garde n'est posee qu'en passant de
 * « rien d'ouvert » a « quelque chose d'ouvert », et retiree seulement si la
 * pile est TOUJOURS vide une fois le geste fini: une couche qui en remplace
 * une autre ne touche pas a l'historique.
 */
import { journal } from '../log/journal.ts'

export interface Couche {
  /** Nom pour le journal. */
  readonly nom: string
  /** Ferme la couche. Appele par « retour »; elle ne doit pas se depiler elle-meme. */
  fermer(): void
}

const pile: Couche[] = []
/** Notre entree de garde est-elle l'entree courante de l'historique? */
let garde = false
/** Retours provoques par le jeu lui-meme (garde retiree): a ignorer. */
let aIgnorer = 0
/** Retrait de garde differe, annule si une couche s'ouvre entre-temps. */
let retrait: ReturnType<typeof setTimeout> | null = null
let branche = false

function poserGarde(): void {
  if (retrait !== null) {
    clearTimeout(retrait)
    retrait = null
  }
  if (garde) return
  garde = true
  history.pushState({ jeuGarde: true }, '')
}

function brancher(): void {
  if (branche) return
  branche = true
  window.addEventListener('popstate', () => {
    if (aIgnorer > 0) {
      aIgnorer--
      // Une couche s'est rouverte pendant que notre retour voyageait: la
      // garde qu'il vient de consommer doit revenir.
      if (pile.length > 0 && !garde) poserGarde()
      return
    }
    // L'utilisateur a appuye sur « retour »: la garde est consommee.
    garde = false
    const c = pile.pop()
    if (!c) return
    journal.input('retour', `touche retour: ${c.nom} ferme`)
    c.fermer()
    // Il reste quelque chose d'ouvert: le prochain « retour » doit encore
    // fermer, pas quitter.
    if (pile.length > 0) poserGarde()
  })
}

/** Une couche vient de s'ouvrir: « retour » la fermera. */
export function empiler(c: Couche): void {
  brancher()
  if (pile.includes(c)) return
  pile.push(c)
  poserGarde()
}

/** La couche s'est fermee d'elle-meme: on retire la garde si plus rien n'est ouvert. */
export function depiler(c: Couche): void {
  const i = pile.lastIndexOf(c)
  if (i < 0) return
  pile.splice(i, 1)
  if (pile.length > 0 || !garde || retrait !== null) return
  // Differe: si ce geste ouvre aussitot une autre couche (menu -> barre de
  // placement), la garde reste en place et l'historique ne bouge pas.
  retrait = setTimeout(() => {
    retrait = null
    if (pile.length > 0 || !garde) return
    garde = false
    aIgnorer++
    history.back()
  }, 0)
}
